/**
 * Authentication Context Provider
 * 
 * Provides authentication state and methods throughout the application
 * using MSAL (Microsoft Authentication Library) for Azure AD integration.
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { loginRequest, getUserId, getUserName, getUserRoles, isAdmin } from './authConfig';

/**
 * Auth context containing authentication state and methods
 */
const AuthContext = createContext({
    isAuthenticated: false,
    isLoading: true,
    userName: null,
    userId: null,
    userRoles: [],
    isAdmin: false,
    login: () => {},
    logout: () => {}
});

/**
 * Custom hook to access authentication context
 * @returns {object} Authentication context with isAuthenticated, userName, userId, login, logout
 */
export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}

/**
 * Authentication Provider component
 * Wraps the application and provides authentication state and methods
 * Handles MSAL redirect flow and auto-login when not authenticated
 */
export function AuthProvider({ children }) {
    const { instance, accounts, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const [isLoading, setIsLoading] = useState(true);
    const [loginAttempted, setLoginAttempted] = useState(false);

    // Get the active account
    const account = accounts[0] || null;

    // Extract user information from account
    const userName = getUserName(account);
    const userId = getUserId(account);
    const userRoles = getUserRoles(account);
    const isUserAdmin = isAdmin(account);

    /**
     * Initiate login redirect flow
     */
    const login = useCallback(async () => {
        if (inProgress !== InteractionStatus.None) {
            console.log('Login already in progress, skipping...');
            return;
        }
        try {
            await instance.loginRedirect(loginRequest);
        } catch (error) {
            console.error('Login failed:', error);
        }
    }, [instance, inProgress]);

    /**
     * Initiate logout redirect flow
     */
    const logout = useCallback(async () => {
        try {
            // Clear session storage to reset filters and state for next login
            sessionStorage.clear();
            await instance.logoutRedirect({
                postLogoutRedirectUri: process.env.REACT_APP_REDIRECT_URI
            });
        } catch (error) {
            console.error('Logout failed:', error);
        }
    }, [instance]);

    /**
     * Update loading state and trigger auto-login once
     */
    useEffect(() => {
        // Still loading if MSAL has operations in progress
        if (inProgress !== InteractionStatus.None) {
            setIsLoading(true);
            return;
        }

        // MSAL is idle - check authentication state
        if (isAuthenticated) {
            setIsLoading(false);
            setLoginAttempted(false);
        } else if (!loginAttempted) {
            // Not authenticated and haven't tried login yet - trigger once
            setLoginAttempted(true);
            console.log('Not authenticated, redirecting to login...');
            login();
        } else {
            // Already attempted login but still not authenticated
            // Show the app (will show loading/redirect message)
            setIsLoading(false);
        }
    }, [isAuthenticated, inProgress, loginAttempted, login]);

    // Context value
    const value = {
        isAuthenticated,
        isLoading,
        userName,
        userId,
        userRoles,
        isAdmin: isUserAdmin,
        login,
        logout
    };

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}

export default AuthContext;
